/**
 * Palette strip — top 5 colors with item counts. Horizontal magazine-style swatch row.
 */
import { store } from '../store.ts';

const COLOR_MAP: Record<string, string> = {
  black: '#171717',
  white: '#f5f4ee',
  grey: '#9a9a9a',
  gray: '#9a9a9a',
  charcoal: '#3a3a3a',
  blue: '#3559b0',
  'navy': '#1d2a4d',
  'midnight blue': '#1d2a4d',
  'dark blue': '#1d2a4d',
  'royal blue': '#1f3d9a',
  beige: '#dac9a8',
  cream: '#efe2bf',
  brown: '#5a3a23',
  tan: '#a87f4a',
  khaki: '#bba672',
  olive: '#6b6b34',
  green: '#2f6741',
  'olive green': '#6b6b34',
  red: '#a52a2a',
  maroon: '#702030',
  burgundy: '#7a2735',
  pink: '#d27a8a',
  rose: '#c66682',
  yellow: '#d6b65e',
  mustard: '#bd8a36',
  orange: '#c8631f',
  saffron: '#c87a2f',
  purple: '#5a3a76',
  indigo: '#3a3070',
  denim: '#3a5a8a',
  'light blue': '#7396cc',
  'sky blue': '#8fb4d2',
  silver: '#bcbcbc',
  gold: '#b88e3a',
  ivory: '#efe7cf',
  ochre: '#b97a2c',
  rust: '#a64428',
  'off white': '#ece6d4',
};

function colorHex(name: string): string {
  const n = name.toLowerCase().trim();
  if (COLOR_MAP[n]) return COLOR_MAP[n];
  for (const key of Object.keys(COLOR_MAP)) {
    if (n.includes(key)) return COLOR_MAP[key] as string;
  }
  return '#9a8d75';
}

export class AlmariPaletteStrip extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.by_color);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byColor: Record<string, number>): void {
    const entries = Object.entries(byColor).sort((a, b) => b[1] - a[1]).slice(0, 6);
    if (!entries.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }

    const total = entries.reduce((s, [, n]) => s + n, 0);
    const swatches = entries.map(([name, count]) => {
      const w = (count / total) * 100;
      const hex = colorHex(name);
      return /* html */ `
<div class="sw" style="--w:${w}%;--bg:${hex};">
  <div class="chip"></div>
  <div class="lbl">${escape(name)}</div>
  <div class="cnt">${count}</div>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 14px;
  padding-left: 4px;
}
.strip {
  display: grid;
  grid-template-columns: repeat(${entries.length}, 1fr);
  gap: 8px;
  background: var(--paper-card);
  border: 1px solid var(--rule);
  border-radius: 8px;
  padding: 18px;
}
.sw { display: grid; gap: 8px; }
.chip {
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg);
  border-radius: 4px;
  border: 1px solid var(--rule);
}
.lbl {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: lowercase;
  color: var(--ink-dim);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cnt {
  font-family: var(--display);
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  text-align: center;
  font-feature-settings: "tnum" 1;
}
</style>
<div class="title">Palette</div>
<div class="strip">${swatches}</div>
`;
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('almari-palette-strip', AlmariPaletteStrip);
